export const phoneRegex = /^\+234\d{10}$/; // Regex pattern for Nigerian phone numbers starting with "+234"
// export const phoneRegex = /^(\+?234|0)?[789]\d{10}$/; // Regex pattern for Nigerian phone numbers +234 or 080

export const formatNaira = (val: number) =>
  val.toLocaleString('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const formatNairaWithoutDecimal = (val: number) =>
  val.toLocaleString('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

export const formatNumber = (val: number) =>
  val.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

export const formateDatetimeStrToNaijaTime = (dateTimeString: string) => {
  if (!dateTimeString) return 'N/A';
  const date = new Date(dateTimeString);
  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    timeZone: 'Africa/Lagos',
  } as Intl.DateTimeFormatOptions;

  return date.toLocaleString('en-NG', options);
};

export const tableFormatDateTimeNG = (dateString: string): string => {
  const date = new Date(dateString);

  const day = date.getDate().toString().padStart(2, '0'); // Day of the month (2 digits)
  const month = date.toLocaleString('en-NG', { month: 'short' }); // Abbreviated month in Nigeria's locale
  const year = date.getFullYear(); // Year

  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0'); // 2-digit minutes

  const ampm = hours >= 12 ? 'pm' : 'am'; // AM/PM notation
  const formattedHour = hours % 12 || 12; // 12-hour format conversion

  return `${day}/${month}/${year} ${formattedHour}:${minutes}${ampm}`;
};

export const tableFormatDateNG = (dateString: string): string => {
  const date = new Date(dateString);

  const day = date.getDate().toString().padStart(2, '0'); // Day of the month (2 digits)
  const month = date.toLocaleString('en-NG', { month: 'short' }); // Abbreviated month in Nigeria's locale
  const year = date.getFullYear(); // Year

  return `${day}/${month}/${year}`;
};

export const tableFormatPhoneNumber = (phoneNumber: string): string => {
  const cleaned = phoneNumber?.replace(/\D/g, '');

  let formattedNumber = cleaned?.startsWith('234')
    ? '0' + cleaned.slice(3)
    : cleaned;

  if (!formattedNumber?.startsWith('0')) {
    formattedNumber = '0' + formattedNumber;
  }

  return formattedNumber?.replace(/(\d{4})(\d{3})(\d{4})/, '$1-$2-$3');
};

export const formatTaxId = (taxId: string): string => {
  // Remove all non-digit characters
  const digitsOnly = taxId.replace(/\D/g, '');

  // If 8 or fewer digits, return as is
  if (digitsOnly.length <= 8) {
    return digitsOnly;
  }

  // If more than 8 digits, format as XXXXXXXX-XXXX
  const firstPart = digitsOnly.slice(0, 8);
  const secondPart = digitsOnly.slice(8, 12); // Limit to 4 digits after hyphen

  return `${firstPart}-${secondPart}`;
};

export const formatToNaijaInt: any = (phoneNumber: string): string => {
  let cleaned = phoneNumber.replace(/[^+\d]/g, '');

  if (cleaned.startsWith('0')) {
    cleaned = '234' + cleaned.slice(1);
  } else if (!cleaned.startsWith('+234')) {
    cleaned = '234' + cleaned;
  }
  return cleaned;
};

export const replaceZeroWith234 = (input: string) => {
  if (input.startsWith('0')) {
    return '234' + input.slice(1);
  }
  return input;
};

export function capitalize(str: string) {
  if (typeof str !== 'string' || str.length === 0) {
    return str;
  }
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function getInitials(firstName?: string, lastName?: string): string {
  const first = firstName?.trim()[0]?.toUpperCase() || '';
  const last = lastName?.trim()[0]?.toUpperCase() || '';
  return `${first}${last}` || 'U'; // 'U' as fallback for "User"
}

export function getTimeOfDayGreeting(): string {
  const hour = new Date().getHours();

  if (hour >= 0 && hour < 12) {
    return 'Good morning';
  } else if (hour >= 12 && hour < 17) {
    return 'Good afternoon';
  } else {
    return 'Good evening';
  }
}

export function capitalizeWords(sentence: string): string {
  if (typeof sentence !== 'string' || sentence.length === 0) {
    return sentence;
  }

  return sentence
    .split(' ')
    .map((word) =>
      word.length > 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : ''
    )
    .join(' ');
}

export function formatForStatus(input: string) {
  return input
    .trim() // Remove leading and trailing whitespace
    .toLowerCase() // Convert the entire string to lowercase
    .split(/[\s_]+/) // Split the string into an array of words
    .filter((word) => word) // Remove any empty elements (in case of multiple spaces)
    .join('-'); // Join the words with hyphens
}

export function formatBadge(str: string): string {
  // Convert the string to lowercase
  let formattedStr = str.toLowerCase();

  // Replace underscores and hyphens with spaces
  formattedStr = formattedStr.replace(/[_-]/g, ' ');

  // Capitalize the first letter of each word
  formattedStr = formattedStr.replace(/\b\w/g, (char) => char.toUpperCase());

  return formattedStr;
}

export function processListOfStrings(stringList: string[]): string[] {
  return stringList.map((str) => {
    // Convert the string to lowercase
    let formattedStr = str.toLowerCase();

    // Replace underscores and hyphens with spaces
    formattedStr = formattedStr.replace(/[_-]/g, ' ');

    // Capitalize the first letter of each word
    formattedStr = formattedStr.replace(/\b\w/g, (char) => char.toUpperCase());

    return formattedStr;
  });
}

export const createObjectFromLists = (
  keys: string[],
  values: string[]
): Record<string, string> => {
  return keys.reduce((acc, key, index) => {
    acc[key] = values[index];
    return acc;
  }, {} as Record<string, string>);
};

export const filterNonEmptyValues = (
  obj: Record<string, any>
): Record<string, any> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== '')
  );
};

export const getVehicleTypes = (): Record<string, string> => ({
  Bicycle: 'BICYCLE',
  Bike: 'BIKE',
  Scooter: 'SCOOTER',
  Car: 'CAR',
  Van: 'VAN',
  Bus: 'BUS',
  Lorry: 'LORRY',
  Truck: 'TRUCK',
});

export const getVehicleTypesFromEnum = (): Record<string, string> => ({
  BICYCLE: 'Bicycle',
  BIKE: 'Bike',
  SCOOTER: 'Scooter',
  CAR: 'Car',
  VAN: 'Van',
  BUS: 'Bus',
  LORRY: 'Lorry',
  TRUCK: 'Truck',
});

export const convertDateFormat = (dateString: string): string => {
  // Ensure the input is in the format "YYYY-MM-DD"
  const [year, month, day] = dateString.split('-');

  // Format and return the date as "DD/MM/YYYY"

  return `${day?.padStart(2, '0')}/${month?.padStart(2, '0')}/${year}`;
};

export function extractDateTime(inputString: string) {
  // Parse the input string into a Date object
  const date = new Date(inputString);

  // Extract date components
  const day = String(date.getDate()).padStart(2, '0'); // Ensure 2 digits
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  // Format date as DD/MMM/YYYY
  const formattedDate = `${day}/${month}/${year}`;

  // Extract time components
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12; // Convert to 12-hour format

  // Format time as HH:MMam/pm
  const formattedTime = `${hours}:${minutes}${ampm}`;

  return { date: formattedDate, time: formattedTime };
}

export const normalizeImageSrc = (value?: string | null) => {
  if (!value) return '';

  const decodedValue = decodeURIComponent(value).trim();

  if (
    !decodedValue ||
    decodedValue === '-' ||
    decodedValue.toLowerCase() === 'image' ||
    !Number.isNaN(Number(decodedValue))
  ) {
    return '';
  }

  return decodedValue;
};

// export function cleanStatusGraphString(input: string) {
//   if (input === null || input === undefined) {
//     return '';
//   }

//   return input
//     .replace(/[_-]/g, ' ')
//     .toLowerCase()
//     .trim();
// }

// Customers are keyed by their WhatsApp number in international form
// ("2348031234567"); people type it locally ("0803 123 4567").
export const toChatId = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('0') ? `234${digits.slice(1)}` : digits;
};

// "2348031234567" → "0803 123 4567"; anything else is returned as is.
export const formatPhone = (chatId?: string | null): string => {
  if (!chatId) return '—';
  const local = /^234\d{10}$/.test(chatId) ? `0${chatId.slice(3)}` : chatId;
  return /^0\d{10}$/.test(local) ? `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7)}` : local;
};

export const looksLikePhone = (text: string) => /^\+?[\d\s-]{7,}$/.test(text.trim());

// "just now", "5m ago", "3h ago", "2d ago".
export const timeAgo = (iso?: string | null): string => {
  if (!iso) return '';
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 60 * 48) return `${Math.floor(minutes / 60)}h ago`;
  return `${Math.floor(minutes / (60 * 24))}d ago`;
};
